import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const PAYMENT_LABELS = { none: "Sans acompte", unpaid: "Non payé", paid: "Acompte payé", refunded: "Remboursé" };
export const PAYMENT_STYLES = {
  none: "bg-black/5 text-[var(--brand-muted)]",
  unpaid: "bg-[#993333]/10 text-[#993333]",
  paid: "bg-[#336633]/10 text-[#336633]",
  refunded: "bg-black/10 text-[var(--brand-muted)]",
};

export default function DepositsPanel() {
  const { settings, refresh } = useSettings();
  const [deposit, setDeposit] = useState(settings.deposit);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDeposit(settings.deposit), [settings]);

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { deposit: { ...deposit, value: Number(deposit.value) } });
      await refresh();
      toast.success("Règle d'acompte enregistrée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const refund = async (b) => {
    if (!window.confirm(`Marquer l'acompte de ${b.client_name} comme remboursé ?`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/refund`);
      toast.success("Acompte marqué comme remboursé.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const withDeposit = bookings.filter((b) => (b.deposit_amount || 0) > 0);

  return (
    <div className="mt-10" data-testid="deposits-panel">
      <h2 className="font-display text-3xl text-[var(--brand-dark)]">Acomptes & paiements</h2>
      <p className="mt-2 max-w-xl text-sm font-light text-[var(--brand-muted)]">
        Activez l'acompte en ligne pour sécuriser vos réservations. La règle par défaut s'applique à toutes les prestations ; vous pouvez la personnaliser prestation par prestation dans l'onglet Prestations.
      </p>

      <div className="mt-8 border border-black/5 bg-[var(--brand-bg)] p-8">
        <label className="flex cursor-pointer items-center gap-3" data-testid="deposit-toggle">
          <input type="checkbox" checked={!!deposit?.enabled}
                 onChange={(e) => setDeposit({ ...deposit, enabled: e.target.checked })}
                 className="h-4 w-4 accent-[#C4839B]" data-testid="deposit-enabled" />
          <span className="text-sm font-medium text-[var(--brand-dark)]">Acompte obligatoire à la réservation</span>
        </label>
        {deposit?.enabled && (
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Type d'acompte par défaut</label>
              <Select value={deposit.mode} onValueChange={(v) => setDeposit({ ...deposit, mode: v })}>
                <SelectTrigger className="mt-2 h-12 rounded-none border-black/10 bg-transparent font-light" data-testid="deposit-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed" data-testid="deposit-mode-fixed">Montant fixe (€)</SelectItem>
                  <SelectItem value="percent" data-testid="deposit-mode-percent">Pourcentage du prix (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">
                {deposit.mode === "percent" ? "Pourcentage (%)" : "Montant (€)"}
              </label>
              <Input type="number" min="1" value={deposit.value}
                     onChange={(e) => setDeposit({ ...deposit, value: e.target.value })}
                     className="mt-2 h-12 rounded-none border-black/10 bg-transparent font-light" data-testid="deposit-value" />
            </div>
          </div>
        )}
        <button onClick={save} disabled={saving}
                className="mt-8 rounded-full bg-[var(--brand-dark)] px-10 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] transition-transform duration-300 hover:scale-[0.97] disabled:opacity-50"
                data-testid="deposit-save-btn">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      <h3 className="font-display mt-14 text-2xl text-[var(--brand-dark)]">Paiements liés aux rendez-vous</h3>
      <div className="mt-6 overflow-x-auto border border-black/5 bg-[var(--brand-bg)]" data-testid="payments-table">
        {withDeposit.length === 0 ? (
          <p className="p-12 text-center text-sm font-light italic text-[var(--brand-muted)]" data-testid="payments-empty">
            Aucun rendez-vous avec acompte pour le moment.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-black/10">
                <TableHead className="text-xs uppercase tracking-[0.2em]">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em]">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em]">Prestation</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em]">Acompte</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em]">Statut</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-[0.2em]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withDeposit.map((b) => (
                <TableRow key={b.id} className="border-black/5" data-testid={`payment-row-${b.id}`}>
                  <TableCell className="font-light">{b.date} · {b.time}</TableCell>
                  <TableCell className="font-light">{b.client_name}</TableCell>
                  <TableCell className="font-light">{b.service_name}</TableCell>
                  <TableCell className="font-display text-lg">{b.deposit_amount} €</TableCell>
                  <TableCell>
                    <span className={`inline-block rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em] ${PAYMENT_STYLES[b.payment_status || "none"]}`} data-testid={`payment-status-${b.id}`}>
                      {PAYMENT_LABELS[b.payment_status || "none"]}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {b.payment_status === "paid" && (
                      <button onClick={() => refund(b)}
                              className="rounded-full border border-black/15 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-dark)] transition-colors hover:border-[var(--brand-accent)]"
                              data-testid={`refund-btn-${b.id}`}>
                        Marquer remboursé
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
